import type { FC, ReactNode } from 'react'
import type { LayoutProps } from '@/types'

import { useCallback, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { getLenis } from '@/lib/smooth-scroll'
import { CursorLabel } from '@/components/generals/cursor-label'
import { MobileMenu } from '@/components/generals/mobile-menu'
import { SiteFooter } from '@/components/generals/site-footer'
import { SiteHeader } from '@/components/generals/site-header'

export const Layout: FC<LayoutProps> = ({ children }): ReactNode => {
    const router = useRouter()
    const [menuOpen, setMenuOpen] = useState(false)

    const closeMenu = useCallback(() => {
        getLenis()?.start()
        document.documentElement.style.removeProperty('overflow')
        setMenuOpen(false)
    }, [])

    const toggleMenu = useCallback(() => {
        if (menuOpen) {
            closeMenu()

            return
        }

        const lenis = getLenis()

        if (lenis) lenis.stop()
        else document.documentElement.style.overflow = 'hidden'

        setMenuOpen(true)
    }, [menuOpen, closeMenu])

    useEffect(() => {
        router.events.on('routeChangeStart', closeMenu)

        return () => router.events.off('routeChangeStart', closeMenu)
    }, [router.events, closeMenu])

    return (
        <div className='flex min-h-screen flex-col'>
            <a
                href='#main'
                className='label fixed left-4 top-4 z-[90] -translate-y-24 bg-signal px-4 py-3 text-ink transition-transform focus-visible:translate-y-0'
            >
                Skip to content
            </a>

            <SiteHeader menuOpen={menuOpen} onMenuToggle={toggleMenu} onNavigate={closeMenu} />
            <MobileMenu open={menuOpen} onNavigate={closeMenu} onClose={closeMenu} />
            <CursorLabel />

            <main id='main' inert={menuOpen} className='relative z-10 flex-1'>
                {children}
            </main>

            <SiteFooter inert={menuOpen} />
        </div>
    )
}
